import { Button, Flex, VStack } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AwardSubCriterionAnswerFormat,
  type AwardCriterionType,
  LibraryElementTypeDto,
  type LibraryAwardSubCriterionDto,
  type LibraryElementDto,
  useCreateLibraryElementMutation,
  useDeleteLibraryElementMutation,
  useUpdateLibraryElementMutation,
} from "../../autogen/bff-api";
import { useApiError } from "../../common/errors/useApiError";
import {
  SUB_CRITERION_HOST_TYPES,
  type AwardSubCriterionSubmitValues,
} from "../../common/library/fields/AwardSubCriterionFields";
import { criterionTypeSelectValue } from "../sourcing-events/buyer/wizard/utils/publicProcurementOptions";
import { useConfirmationModal } from "../../common/useConfirmationModal";
import { AwardSubCriterionList } from "./AwardSubCriterionList";
import { AwardSubCriterionModal, type AwardSubCriterionFormValues } from "./AwardSubCriterionModal";
import { LibrarySourceSection } from "./LibrarySourceSection";

// t('Your requirements') t('SourceMagnet standard') t('Add sub-criterion')
// t('Delete sub-criterion') t('Are you sure you want to delete this requirement?') t('Delete')
// t('No sub-criteria of your own yet.') t('No SourceMagnet standard requirements yet.')
// t('All')

const ALL = "ALL";

interface Props {
  elements: LibraryElementDto[];
  orgName: string;
  canManageOrg: boolean;
  addRefusal?: string;
  isSystemAdmin: boolean;
}

type DrawerState =
  | { kind: "add"; global: boolean }
  | { kind: "edit"; element: LibraryElementDto; initialValues: AwardSubCriterionFormValues };

interface Row {
  element: LibraryElementDto;
  value: LibraryAwardSubCriterionDto;
}

const rowsOf = (elements: LibraryElementDto[]): Row[] =>
  elements.flatMap((element) =>
    element.content.awardSubCriterion ? [{ element, value: element.content.awardSubCriterion }] : []
  );

/**
 * Standalone requirements ("underkrav"), each tagged with the kind of criterion it belongs under. That
 * tag is what lets the wizard's "add from the library" inside a criterion offer only what fits there.
 */
export const AwardSubCriterionLibraryTab = ({ elements, orgName, canManageOrg, addRefusal, isSystemAdmin }: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const confirmationModal = useConfirmationModal();
  const [createLibraryElement, { isLoading: isCreating }] = useCreateLibraryElementMutation();
  const [updateLibraryElement, { isLoading: isUpdating }] = useUpdateLibraryElementMutation();
  const [deleteLibraryElement] = useDeleteLibraryElementMutation();
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [drawerKey, setDrawerKey] = useState(0);
  const [typeFilter, setTypeFilter] = useState<AwardCriterionType | typeof ALL>(ALL);

  const requirements = elements.filter((e) => e.content.type === LibraryElementTypeDto.AwardSubCriterion);
  // Compared through criterionTypeSelectValue, so the environment sub-types answer to the one Miljø
  // chip exactly as they do everywhere else the type is picked.
  const matchesFilter = (row: Row) =>
    typeFilter === ALL ||
    criterionTypeSelectValue(row.value.criterionType) === criterionTypeSelectValue(typeFilter);
  const orgRows = rowsOf(requirements.filter((e) => !e.owner.global)).filter(matchesFilter);
  const globalRows = rowsOf(requirements.filter((e) => e.owner.global)).filter(matchesFilter);

  const openAdd = (global: boolean) => {
    setDrawerKey((k) => k + 1);
    setDrawer({ kind: "add", global });
  };

  const openEdit = (element: LibraryElementDto, value: LibraryAwardSubCriterionDto) => {
    setDrawerKey((k) => k + 1);
    setDrawer({
      kind: "edit",
      element,
      initialValues: {
        criterionType: value.criterionType,
        name: value.name,
        description: value.description,
        // The modal omits the weight field; the form still holds the key, so it needs an empty seat.
        weight: "",
        answerFormat: value.answerFormat ?? AwardSubCriterionAnswerFormat.FreeText,
        requiresDocumentation: value.requiresDocumentation ?? false,
      },
    });
  };

  const handleSubmit = async (values: AwardSubCriterionSubmitValues) => {
    // The form only omits the criterion type when a criterion supplies it, which never happens here.
    if (!values.criterionType) return;
    const awardSubCriterion: LibraryAwardSubCriterionDto = {
      criterionType: values.criterionType,
      name: values.name ?? "",
      description: values.description,
      answerFormat: values.answerFormat,
      requiresDocumentation: values.requiresDocumentation,
    };
    try {
      if (drawer?.kind === "add") {
        await createLibraryElement({
          createLibraryElementRequest: {
            content: { type: LibraryElementTypeDto.AwardSubCriterion, awardSubCriterion },
            global: drawer.global,
          },
        }).unwrap();
      } else if (drawer?.kind === "edit") {
        await updateLibraryElement({
          updateLibraryElementRequest: {
            id: drawer.element.id,
            content: {
              type: LibraryElementTypeDto.AwardSubCriterion,
              awardSubCriterion: {
                ...awardSubCriterion,
                id: drawer.element.content.awardSubCriterion?.id ?? undefined,
              },
            },
            tags: drawer.element.tags,
          },
        }).unwrap();
      }
      setDrawer(null);
    } catch (e) {
      trigger(e);
    }
  };

  const handleDelete = (element: LibraryElementDto) => {
    confirmationModal.show({
      title: t("Delete sub-criterion"),
      description: t("Are you sure you want to delete this requirement?"),
      confirmButtonText: t("Delete") ?? "",
      confirmButtonColor: "red",
      onConfirmed: async () => {
        try {
          await deleteLibraryElement({ id: element.id }).unwrap();
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  // Same chip as the library panel's source filter, not a fourth variant of the shape.
  const chip = (value: AwardCriterionType | typeof ALL, label: string) => (
    <Button
      key={value}
      size="xs"
      borderRadius="full"
      variant={typeFilter === value ? "solid" : "outline"}
      colorScheme={typeFilter === value ? "teal" : "gray"}
      aria-pressed={typeFilter === value}
      onClick={() => setTypeFilter(value)}
    >
      {label}
    </Button>
  );

  return (
    <VStack align="stretch" spacing="8">
      {/* The type decides which criteria a requirement can be used under, so it is what the list is
          worth narrowing by once there are more than a handful. */}
      <Flex gap="2" wrap="wrap">
        {chip(ALL, t("All"))}
        {SUB_CRITERION_HOST_TYPES.map((type) => chip(type, t(type)))}
      </Flex>

      <LibrarySourceSection
        title={t("Your requirements")}
        scope="org"
        orgName={orgName}
        editable={canManageOrg}
        addRefusal={addRefusal}
        addLabel={t("New sub-criterion")}
        onAdd={() => openAdd(false)}
        isEmpty={orgRows.length === 0}
        emptyText={t("No sub-criteria of your own yet.")}
      >
        <AwardSubCriterionList
          items={orgRows.map((r) => r.value)}
          readOnly={!canManageOrg}
          onAdd={() => openAdd(false)}
          onEdit={(i) => openEdit(orgRows[i].element, orgRows[i].value)}
          onDelete={(i) => handleDelete(orgRows[i].element)}
        />
      </LibrarySourceSection>

      <LibrarySourceSection
        title={t("SourceMagnet standard")}
        scope="global"
        editable={isSystemAdmin}
        addRefusal={addRefusal}
        addLabel={t("New sub-criterion")}
        onAdd={() => openAdd(true)}
        isEmpty={globalRows.length === 0}
        emptyText={t("No SourceMagnet standard requirements yet.")}
      >
        <AwardSubCriterionList
          items={globalRows.map((r) => r.value)}
          readOnly={!isSystemAdmin}
          onAdd={() => openAdd(true)}
          onEdit={(i) => openEdit(globalRows[i].element, globalRows[i].value)}
          onDelete={(i) => handleDelete(globalRows[i].element)}
        />
      </LibrarySourceSection>

      <AwardSubCriterionModal
        key={drawerKey}
        isOpen={drawer !== null}
        isSaving={isCreating || isUpdating}
        mode={drawer?.kind ?? "add"}
        defaultValues={drawer?.kind === "edit" ? drawer.initialValues : undefined}
        onSubmit={handleSubmit}
        onClose={() => setDrawer(null)}
      />
      {confirmationModal.modal}
    </VStack>
  );
};
